import angular from 'angular';
import extend from 'extend';
import shuffle from 'lodash/shuffle';
import mapItem from '../../lib/mapItem';
import mapTempItem from '../../lib/mapTempItem';
import currentItemInfo from '../../lib/currentItemInfo';
import { getProductAliasFromType } from '../../lib/productHelpers';
import {
  saveDisplayedReturnReasons,
  saveNestedDisplayedReturnReasons,
  saveSelectedReturnReason,
  saveSelectedNestedReturnReason,
} from '../../actions/returnReasonsMonitoring';
import { toggleMaxItemsReached } from '../../actions/restrictions';
import getPrevSelectedItemCount from '../../lib/getPrevSelectedItemCount';
import {
  receiveImages,
  deleteCachedImages,
  resetPhotoUploadState,
} from '../../actions/photoUpload';
import { QTY_FIELD_ACTION_MODES, QTY_WITH_EXPIRY_INPUT } from '../../constants/fieldTypes';
import { EXPIRATION_DATE_PATTERN } from '../../constants/validationPatterns';
import patternInputValidation from '../../lib/patternInputValidation';
import generateKey from '../../lib/generateKey';
import { VERIFY_ELIGIBILITY } from '../../constants/endpoints';
import { DEFAULT_MAX_COMMENTS_COUNT } from '../../constants/defaultSettings';
import { getNestedReasons, parseReasonJson, partitionReasonsByProduct } from '../../lib/reasons';
import { returnItemsService, retailerSettingsService } from '../../services';
import { setDisableSidebarComments, updateSidebarState } from '../../actions/returnReasonSidebar';
import { store } from '../../store';
import { ActionTypes } from '../../constants/enums';
import { refreshOrderEligibilities, fetchBundleItems, setItems } from '../../actions/orderService';
import { setIsFetchingAddCartItem, setIsFetchingRemoveCartItem } from '../../actions/itemsPage';
import { deepClone } from '../../lib/stringHelpers';

const returnReasonSidebarComponent = () => {
  const {
    language,
    returnsSettings: {
      returns: {
        ineligible_reasons: ineligibleReasons = {},
        disableSidebarComments,
        randomizeReasonOrder,
        sidebar_comments: {
          returns: { comments_count: maxCommentsCount = DEFAULT_MAX_COMMENTS_COUNT } = {},
        } = {},
      } = {},
    },
    returnReasonSidebar,
  } = store.getState();
  store.dispatch(setDisableSidebarComments(disableSidebarComments));
  const node = document.createElement('div');
  document.body.appendChild(node);
  let updateToggleExchangeCallback = null;
  let updateSelectionCallback = null;
  let resetSelectedProductCallback = null;

  store.dispatch(updateSidebarState(returnReasonSidebar));

  const updateState = (newState, deep = true) => {
    const { returnReasonSidebar } = store.getState();
    store.dispatch(updateSidebarState(extend(deep, returnReasonSidebar, newState)));
    renderSidebar();
  };

  const generateExpiryObject = () => ({
    key: generateKey(),
    values: { qty: '', expirationDate: '' },
    valid: false,
  });

  const generateQuantityFieldInitialState = (quantityField) => {
    if (!quantityField) return { expiryList: [], quantityFieldAction: '' };
    const { type, validate } = quantityField;
    return {
      expiryList: type === QTY_WITH_EXPIRY_INPUT ? [generateExpiryObject()] : [],
      quantityFieldAction: validate ? QTY_FIELD_ACTION_MODES.DISABLE_VERIFY : '',
      validationMessage: '',
    };
  };

  const actions = {
    initialize: (item, isExchange) => updateState(itemInit(item, isExchange), false),

    toggle: () => updateState(toggleSidebar()),

    selectReason: (index, nested) => updateState(checkReason(index, nested), false),

    updateComment: (commentData) => updateState(updateReturnComment(commentData)),

    saveItem: async () => {
      const updatedItem = await saveItem();
      updateState(updatedItem);
      actions.toggle();
    },

    deleteItem: async () => {
      const updates = await cancelItem();
      updateState(updates);
    },

    saveItemProgress: () => {
      const {
        returnReasonSidebar: { currentItem },
        returnReasonSidebar,
      } = store.getState();
      updateState({
        temporaryItems: {
          [currentItem.item_id]: currentItemInfo(returnReasonSidebar),
        },
      });
    },

    resetSidebar: () => updateState({ open: false, temporaryItems: {}, savedItems: {} }, false),

    resetItemInfo: () => updateState(currentItemInfo({})),

    revertReasons: () => updateState(resetReasons()),

    selectQuantity: ({ target: { value } }) => updateState({ returnQuantity: +value }),

    handleOnQtyChange: ({ target: { value } }) => {
      const { itemQuantity } = store.getState().returnReasonSidebar;
      if (parseInt(value, 10) > itemQuantity) return;
      updateState({ returnQuantity: +value });
    },

    handleExpiryGroupChange: (name, value) => {
      const { expiryList } = store.getState().returnReasonSidebar;
      const newExpiry = [...expiryList];
      const { values, valid } = newExpiry[newExpiry.length - 1];

      values[name] = value;

      const allFieldsFilled = Object.keys(values).every((key) => values[key]);
      const validDateFormat = patternInputValidation(
        values.expirationDate,
        EXPIRATION_DATE_PATTERN,
      );

      let quantityFieldAction = QTY_FIELD_ACTION_MODES.DISABLE_VERIFY;

      if (allFieldsFilled && validDateFormat) {
        quantityFieldAction = QTY_FIELD_ACTION_MODES.ENABLE_VERIFY;
      }

      if (valid) {
        quantityFieldAction = QTY_FIELD_ACTION_MODES.ENABLE_ADD;
      }

      updateState(
        {
          expiryList: newExpiry,
          returnQuantity: expiryList.reduce(
            (sum, { values: { qty = 0 } }) => sum + parseInt(qty, 10),
            0,
          ),
          quantityFieldAction,
        },
        false,
      );
    },

    handleVerifyQtyField: async () => {
      const {
        quantityField: { type },
      } = store.getState().returnReasonSidebar;
      try {
        const { expiryList, itemId, returnQuantity, itemQuantity } =
          store.getState().returnReasonSidebar;
        const {
          page: { browserFingerprint, giftOrder, locale, isSimpleReturns },
          retailerSettings: { name: retailerName } = {},
          orderInfo: { currentOrderNumber },
        } = store.getState();
        const { values: { qty, expirationDate } = {} } = expiryList[expiryList.length - 1];

        if (returnQuantity > itemQuantity) {
          return updateState(
            {
              error: {
                field: 'qty',
                message:
                  language[`returns.step_1.sidepanel_quantity_field.${type}.qty.validation_error`],
              },
            },
            false,
          );
        }

        updateState(
          {
            isVerifying: true,
            error: {},
          },
          false,
        );

        const response = await fetch(VERIFY_ELIGIBILITY(retailerName), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'narvar-tfid': browserFingerprint,
          },
          body: JSON.stringify({
            exchange: false,
            expiry_date: expirationDate,
            gift: giftOrder,
            item_id: itemId,
            order_number: currentOrderNumber,
            request_locale: locale,
            return_qty: qty,
            return_reason_code_id: getReasonInfo().return_reason_code_id,
            simple_return: isSimpleReturns,
          }),
        });

        const { eligibility: eligible, eligibility_message: eligibilityMsg } =
          await response.json();

        expiryList[expiryList.length - 1].valid = eligible;

        if (eligible) {
          return updateState(
            {
              isVerifying: false,
              expiryList,
              quantityFieldAction: QTY_FIELD_ACTION_MODES.ENABLE_ADD,
              error: {},
            },
            false,
          );
        }

        return updateState(
          {
            isVerifying: false,
            error: {
              field: 'expirationDate',
              message: eligibilityMsg,
            },
            quantityFieldAction: QTY_FIELD_ACTION_MODES.ENABLE_VERIFY,
          },
          false,
        );
      } catch (err) {
        return updateState(
          {
            isVerifying: false,
            quantityFieldAction: QTY_FIELD_ACTION_MODES.ENABLE_VERIFY,
            error: {
              field: null,
              message:
                language[`returns.step_1.sidepanel_quantity_field.${type}.unavailable_error`],
            },
          },
          false,
        );
      }
    },

    handleAddExpiryGroup: () => {
      const {
        returnReasonSidebar: { expiryList },
      } = store.getState();
      updateState(
        {
          expiryList: [...expiryList, generateExpiryObject()],
          quantityFieldAction: QTY_FIELD_ACTION_MODES.DISABLE_VERIFY,
        },
        false,
      );
    },

    removeGroup: (removeIdx) => {
      const {
        returnReasonSidebar: { expiryList, isVerifying, returnQuantity, quantityFieldAction },
      } = store.getState();

      if (isVerifying) return;

      const {
        values: { qty: removedQty },
      } = expiryList[removeIdx];
      const newExpiryList = expiryList.filter((_, idx) => idx !== removeIdx);

      updateState(
        newExpiryList.length
          ? {
              expiryList: newExpiryList,
              error: {},
              returnQuantity: returnQuantity - removedQty,
              quantityFieldAction: newExpiryList.every(({ valid }) => valid)
                ? QTY_FIELD_ACTION_MODES.ENABLE_ADD
                : quantityFieldAction,
            }
          : {
              expiryList: [generateExpiryObject()],
              quantityFieldAction: QTY_FIELD_ACTION_MODES.DISABLE_VERIFY,
              error: {},
            },
        false,
      );
    },
  };

  const setUpdateCallback = (toggleExchangeCallback, selectionCallback, resetProductCallback) => {
    updateToggleExchangeCallback = toggleExchangeCallback;
    updateSelectionCallback = selectionCallback;
    resetSelectedProductCallback = resetProductCallback;
  };

  const generateEligibleQtyList = (quantity) => {
    const {
      restrictions: { qtyPerItemLimit },
    } = store.getState();
    const quantitySelect = [];
    const selectableQty = Math.min(quantity, qtyPerItemLimit);

    for (let i = 0; i < selectableQty; i += 1) {
      quantitySelect.push(i + 1);
    }

    return quantitySelect;
  };

  const itemInit = (item, isExchange) => {
    if (item.uploadedImages) {
      store.dispatch(receiveImages(item.uploadedImages));
    }
    const {
      itemId,
      itemName,
      itemProperties,
      itemQuantity,
      returnReasonCodes,
      exchangeEligibility,
    } = mapItem(item);
    const { temporaryItems, selectedProduct } = store.getState().returnReasonSidebar;
    const tempItem = temporaryItems[itemId];
    const { returnReasons } = partitionReasonsByProduct(returnReasonCodes);
    const reasonCodes = randomizeReasonOrder ? shuffle(returnReasons) : returnReasons;

    const itemUpdate = {
      currentItem: { ...(tempItem || item), itemName, itemProperties },
      itemProperties,
      temporaryItems,
      itemQuantity,
      itemId,
      eligibleQtyList: generateEligibleQtyList(itemQuantity),
      returnReasonCodes: reasonCodes,
      isExchange,
      exchangeEligibility,
      selectedProduct,
      itemFinalized: false,
    };

    if (!tempItem) {
      itemUpdate.temporaryItems[itemId] = { ...item };
    } else {
      extend(itemUpdate, mapTempItem(tempItem));
    }

    if (itemUpdate.returnReasonCodes) {
      store.dispatch(saveDisplayedReturnReasons(itemUpdate.returnReasonCodes));
    }

    return itemUpdate;
  };

  const toggleSidebar = () => {
    const {
      restrictions: { maxItemsReached },
      photoUpload: { isUploading },
      returnReasonSidebar: { open, itemFinalized, currentItem, isExchange, itemId },
      itemsPage: { isFetchingAddCartItem, isFetchingRemoveCartItem },
    } = store.getState();
    const isFetching = isUploading || isFetchingAddCartItem || isFetchingRemoveCartItem;

    if (isFetching) {
      return { open: true };
    }

    if (open && maxItemsReached && !itemFinalized) {
      return { open: true };
    }

    if (!currentItem) {
      return { open: false };
    }

    if (isExchange && currentItem.resetSelectedProduct) {
      resetSelectedProductCallback(itemId);
    }

    if (open) {
      actions.saveItemProgress();
      actions.resetItemInfo();
      store.dispatch(resetPhotoUploadState());
    }

    return { open: !open };
  };

  const updateBundleItems = async () => {
    const {
      orderService: { bundleItems = {}, items: orderItems },
    } = store.getState();

    const items = returnItemsService.getItems();

    const updatedItems = orderItems.map((orderItem) => {
      const bundleItem = bundleItems[orderItem.item_id];
      const existingItem = items.find((item) => `${item.item_id}` === `${orderItem.item_id}`);

      if (bundleItem) {
        if (!existingItem) {
          const newItem = {
            ...orderItem,
            selected: true,
            return_qty: bundleItem.quantity,
            total_price: bundleItem.quantity * orderItem.item_price,
          };
          returnItemsService.addItem(newItem);
          return newItem;
        }
        return existingItem;
      }

      if (existingItem) {
        returnItemsService.removeItemById(orderItem);
        return {
          ...orderItem,
          selected: false,
          return_qty: 0,
          total_price: 0,
        };
      }
      return orderItem;
    });

    store.dispatch(setItems(updatedItems));
  };

  const cancelItem = async ({ overrideToggle, preventEligibilityRefresh } = {}) => {
    const {
      restrictions: { itemLimit },
      claims: { enabled: claimsEnabled },
      itemsPage: { isFetchingRemoveCartItem },
    } = store.getState();
    if (isFetchingRemoveCartItem) return {};

    const { currentItem, savedItems, temporaryItems } = store.getState().returnReasonSidebar;
    const selectedIem = deepClone(currentItem);
    selectedIem.selected = false;
    selectedIem.selectedProduct = null;
    selectedIem.returnQuantity = 0;
    returnItemsService.removeItemById(selectedIem);

    const { bundles: { enabled: bundlesEnabled } = {} } = retailerSettingsService.getSettings();

    if (claimsEnabled) {
      try {
        store.dispatch(setIsFetchingRemoveCartItem(true));
        if (!preventEligibilityRefresh) {
          await store.dispatch(refreshOrderEligibilities(returnItemsService.getItems()));
        }
      } catch (err) {
        console.log(err);
      } finally {
        store.dispatch(setIsFetchingRemoveCartItem(false));
      }
    }

    if (bundlesEnabled) {
      try {
        store.dispatch(setIsFetchingAddCartItem(true));
        await store.dispatch(fetchBundleItems(returnItemsService.getItems()));
        await updateBundleItems();
      } catch (err) {
        console.log(err);
      } finally {
        store.dispatch(setIsFetchingAddCartItem(false));
        returnItemsService.updateCartActions();
      }
    }

    actions.revertReasons();

    delete savedItems[selectedIem.item_id];
    const newItemCount = Object.keys(savedItems).length;
    const maxItemsReached = newItemCount === itemLimit;
    store.dispatch(toggleMaxItemsReached(maxItemsReached));

    // TODO: Remove asap. This is hack that also pushes additional items as true.
    updateSelectionCallback(selectedIem, true, true);

    if (!overrideToggle) {
      selectedIem.resetSelectedProduct = true;
      actions.toggle();
    }

    return {
      savedItems,
      temporaryItems: { ...temporaryItems, [selectedIem.item_id]: null },
      returnQuantity: 0,
    };
  };

  const resetReasons = () => ({
    selectedReason: null,
    selectedNestedReason: null,
    nestedReasons: null,
  });

  const chooseReason = (index) => {
    const selectedReason = store.getState().returnReasonSidebar.returnReasonCodes[index];
    const reasonJsonStr = selectedReason.return_reasons[0].return_reason_settings_json;
    const {
      commentHidden,
      commentRequired,
      commentPlaceholder,
      quantityField,
      photoUploadEnabled,
      photoUploadRequired,
      photoUploadPlaceholder,
      photoUploadTitle,
      photoUploadMaxFiles,
    } = parseReasonJson(reasonJsonStr);
    const nestedReasons = getNestedReasons(selectedReason, randomizeReasonOrder);
    const nestedReasonsTitle = nestedReasons
      ? nestedReasons.reduce(
          (acc, reason) =>
            parseReasonJson(reason.return_reason_settings_json).nestedReasonsTitle || acc,
          '',
        )
      : '';
    store.dispatch(saveNestedDisplayedReturnReasons(nestedReasons));
    store.dispatch(saveSelectedReturnReason(selectedReason));

    // TODO: Rewrite this. This is for disabling the next button when changing from a normal
    // reason to a comment required reason.
    if (commentRequired) {
      window.setTimeout(() => angular.element('#returnComment').focus(), 100);
      cancelItem({ overrideToggle: true, preventEligibilityRefresh: true });
    }

    return {
      selectedReason,
      commentHidden,
      commentRequired,
      commentPlaceholder,
      nestedReasonsTitle,
      nestedReasons,
      quantityField,
      photoUploadEnabled,
      photoUploadRequired,
      photoUploadPlaceholder,
      photoUploadTitle,
      photoUploadMaxFiles,
      ...generateQuantityFieldInitialState(quantityField),
    };
  };

  const chooseNestedReason = (index) => {
    const selectedNestedReason = store.getState().returnReasonSidebar.nestedReasons[index];
    const reasonJsonStr = selectedNestedReason.return_reason_settings_json;
    const {
      commentHidden,
      commentRequired,
      commentPlaceholder,
      quantityField,
      photoUploadEnabled,
      photoUploadRequired,
      photoUploadPlaceholder,
      photoUploadTitle,
      photoUploadMaxFiles,
    } = parseReasonJson(reasonJsonStr);
    store.dispatch(saveSelectedNestedReturnReason(selectedNestedReason));

    return {
      selectedNestedReason,
      commentHidden,
      commentRequired,
      commentPlaceholder,
      quantityField,
      photoUploadEnabled,
      photoUploadRequired,
      photoUploadPlaceholder,
      photoUploadTitle,
      photoUploadMaxFiles,
      ...generateQuantityFieldInitialState(quantityField),
    };
  };

  const checkReason = (index) => {
    const {
      returnReasonSidebar: { nestedReasons, photoUploadEnabled },
    } = store.getState();
    if (photoUploadEnabled) {
      store.dispatch(deleteCachedImages());
    }
    if (!nestedReasons) {
      return chooseReason(index);
    }
    return chooseNestedReason(index);
  };

  const updateReturnComment = (newComment) => {
    if (!newComment) {
      return { returnComment: '' };
    }
    if (newComment.length > maxCommentsCount) {
      return { returnComment: newComment.slice(0, maxCommentsCount) };
    }

    return { returnComment: newComment };
  };

  const getReasonInfo = () => {
    const {
      returnReasonSidebar: { selectedReason, selectedNestedReason, returnComment, commentHidden },
    } = store.getState();
    const reasonInfo = {};

    if (selectedNestedReason) {
      reasonInfo.return_reason_code_id = selectedNestedReason.return_reason_code_id;
      reasonInfo.return_desc = selectedNestedReason.display_name;
    } else {
      reasonInfo.return_reason_code_id = selectedReason.return_reasons[0].return_reason_code_id;
      reasonInfo.return_desc = selectedReason.reason_category;
    }

    reasonInfo.user_comment = commentHidden ? '' : returnComment;

    return reasonInfo;
  };

  const saveItem = async () => {
    const {
      restrictions: { itemLimit },
      photoUpload: { images },
      claims: { enabled: claimsEnabled },
    } = store.getState();

    const { bundles: { enabled: bundlesEnabled } = {} } = retailerSettingsService.getSettings();

    const {
      currentItem,
      returnQuantity,
      savedItems,
      selectedReason,
      selectedNestedReason,
      expiryList,
    } = store.getState().returnReasonSidebar;

    if (!currentItem) return null;

    let selectedItem = deepClone(savedItems[currentItem.item_id]);

    if (!selectedItem) {
      selectedItem = currentItem;
    }

    const selectedItemId = selectedItem.item_id;
    const selectUpdate = {
      ...store.getState().returnReasonSidebar.saveditems,
      itemFinalized: true,
    };
    let reasonId;
    let inValidReasonSelected = false;
    selectedItem.ineligibilityInfo = null;
    selectedItem.product = getProductAliasFromType('return');
    if (
      selectedNestedReason &&
      ineligibleReasons.attribute_check &&
      ineligibleReasons.return_reason_attribute === 'reason_category'
    ) {
      reasonId = selectedReason.reason_category;
    } else if (selectedNestedReason) {
      reasonId = selectedNestedReason.retailer_reason_code;
    } else {
      reasonId = selectedReason.return_reasons[0].retailer_reason_code;
    }

    returnItemsService.removeItemById(selectedItem);

    if (!ineligibleReasons[reasonId]) {
      selectedItem.selected = true;
      extend(selectedItem, getReasonInfo());
      selectedItem.return_qty = returnQuantity || 1;
      selectedItem.total_price = selectedItem.return_qty * selectedItem.item_price;
      returnItemsService.addItem(selectedItem);
      extend(selectUpdate, {
        savedItems: { [selectedItemId]: selectedItem },
      });
    } else if (ineligibleReasons.attribute_check) {
      ineligibleReasons[reasonId].map((item) => {
        if (item.categories.indexOf(currentItem[ineligibleReasons.attribute_name]) !== -1) {
          inValidReasonSelected = true;
          selectedItem.selected = false;
          selectedItem.ineligibilityInfo = item;
        }
      });

      if (!inValidReasonSelected) {
        selectedItem.selected = true;
        extend(selectedItem, getReasonInfo());
        selectedItem.return_qty = returnQuantity || 1;
        selectedItem.total_price = selectedItem.return_qty * selectedItem.item_price;
        returnItemsService.addItem(selectedItem);
        extend(selectUpdate, {
          savedItems: { [selectedItemId]: selectedItem },
        });
      }
    } else {
      selectedItem.selected = false;
      selectedItem.ineligibilityInfo = ineligibleReasons[reasonId];
    }

    if (images.length) {
      // Used only by FE to keep track of the path and filename
      selectedItem.uploadedImages = images;

      // BE stores this into the return_process_item_json
      selectedItem.item_images = images.map(({ filename }) => filename);
      store.dispatch(resetPhotoUploadState());
    }

    if (expiryList.length > 0) {
      selectedItem.itemized_details = expiryList.map(({ values: { qty, expirationDate } }) => ({
        quantity: qty,
        expiry_date: expirationDate,
      }));
    }

    const prevSelectedItemCount = getPrevSelectedItemCount(savedItems, selectedItemId);
    const maxItemsReached = prevSelectedItemCount + 1 === itemLimit;
    if (maxItemsReached) {
      store.dispatch(toggleMaxItemsReached(maxItemsReached));
    }

    if (claimsEnabled) {
      selectedItem.action_type = ActionTypes.Return;
      selectedItem.action_reason_id = `${selectedItem.return_reason_code_id}`;
      delete selectedItem.return_reason_code_id;
      try {
        store.dispatch(setIsFetchingAddCartItem(true));
        await store.dispatch(refreshOrderEligibilities(returnItemsService.getItems()));
      } catch (err) {
        console.log(err);
      } finally {
        store.dispatch(setIsFetchingAddCartItem(false));
        returnItemsService.updateCartActions();
      }
    }

    if (bundlesEnabled) {
      try {
        store.dispatch(setIsFetchingAddCartItem(true));
        await store.dispatch(fetchBundleItems(returnItemsService.getItems()));
        await updateBundleItems();
      } catch (err) {
        console.log(err);
      } finally {
        store.dispatch(setIsFetchingAddCartItem(false));
        returnItemsService.updateCartActions();
      }
    }

    updateSelectionCallback(selectedItem, !maxItemsReached, false);
    return selectUpdate;
  };

  const reasonCodeSelected = (returnReason, displayProperty) => {
    const { selectedReason, selectedNestedReason: selectedNested } =
      store.getState().returnReasonSidebar;
    const reason = returnReason[displayProperty];
    const reasonSelected = selectedReason && reason === selectedReason[displayProperty];
    const nestedSelected = selectedNested && reason === selectedNested[displayProperty];
    return reasonSelected || nestedSelected;
  };

  const showQuantityFields = () => {
    const { nestedReasons, selectedReason, selectedNestedReason } =
      store.getState().returnReasonSidebar;
    return (selectedReason && !nestedReasons) || selectedNestedReason;
  };

  const commentBoxTitle = (title) => {
    const { eligibleQtyList } = store.getState().returnReasonSidebar;
    const incrementStep = eligibleQtyList && eligibleQtyList.length > 1;
    return incrementStep ? `3. ${title}` : `2. ${title}`;
  };

  const handleSelectProduct = () => {
    const { temporaryItems, itemId } = store.getState().returnReasonSidebar;
    updateToggleExchangeCallback(temporaryItems[itemId], null);
    actions.toggle();
  };

  const renderSidebar = async () => {
    const handlers = {
      reasonCodeSelected,
      showQuantityFields,
      commentBoxTitle,
      handleSelectProduct,
      actions,
    };
    const module = await import(
      /* webpackChunkName: "return-reason-sidebar" */ `../../components/ReturnReasonSidebar/returnReasonSidebar`
    );
    module.renderReturnReasonSidebar({
      handlers,
      node,
    });
  };

  return {
    actions,
    setUpdateCallback,
  };
};

export const returnReasonSidebar = returnReasonSidebarComponent();
